/** Shared history ordering: both domains sort their complete scoped result before paging or CSV export. */
import { EntityViewSort } from '../../entities/views/entity.view';
import { EntityViewSortDirection } from '../../entities/views/enums';
import { ServiceHistoryRow } from './visit.service.history';
import { ScheduleFrequencyBand } from '../../../scheduling/schedule.rule';

const textOrder = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
const fields: Record<string, (row: ServiceHistoryRow) => string | number | undefined> = {
    siteZone: row => row.siteZone,
    zone: row => row.siteZone ?? row.zone?.name,
    siteCategory: row => row.siteCategory?.name,
    siteAddress: row => row.siteAddress,
    site: row => row.site?.name,
    serviceType: row => row.serviceType?.name,
    serviceScope: row => row.serviceScope?.name,
    // Band order (Daily → Annually), never alphabetical: the enum's declaration order is the rank.
    frequency: row => row.frequency ? Object.values(ScheduleFrequencyBand).indexOf(row.frequency) : undefined,
    name: row => row.name,
    job: row => row.job?.name,
    resolution: row => row.resolution?.name,
    date: row => row.date,
    dueDate: row => row.dueDate,
    rescheduledDate: row => row.rescheduledDate,
    completedOn: row => row.completedOn,
    overdueDays: row => row.overdueDays,
    resolutionReason: row => row.resolutionReason,
    resolutionNotes: row => row.resolutionNotes,
    serviceLine: row => row.serviceLine?.name,
    approvedFor: row => row.approvedFor,
    earliest: row => row.earliest,
    latest: row => row.latest,
    rule: row => row.rule,
    result: row => row.resultLabel,
    completionStatus: row => row.completionStatus,
};

/** Sorts without mutating query rows; blanks stay last in either direction and ids break equal-value ties. */
export function sortServiceHistoryRows(rows: readonly ServiceHistoryRow[], sort?: EntityViewSort): ServiceHistoryRow[] {

    const field = sort?._id && Object.prototype.hasOwnProperty.call(fields, sort._id) ? fields[sort._id] : undefined;
    if (!field) return [...rows];
    const valueOf = field;
    const direction = sort?.direction === EntityViewSortDirection.Asc ? 1 : -1;
    return [...rows].sort((left, right) => {
        const a = valueOf(left);
        const b = valueOf(right);
        if (a == null || a === '') {
            if (b != null && b !== '') return 1;
        } else if (b == null || b === '') {
            return -1;
        } else {
            const compared = typeof a === 'number' && typeof b === 'number' ? a - b : textOrder.compare(String(a), String(b));
            if (compared) return compared * direction;
        }
        return right.date.localeCompare(left.date) || textOrder.compare(left.name, right.name) || left._id.localeCompare(right._id);
    });
}
